import React, { useEffect, useRef, useState } from 'react';

function isDirectPdfSrc(src) {
  return typeof src === 'string' && (src.startsWith('blob:') || src.startsWith('data:'));
}

function pdfBlobFromBytes(blob) {
  if (!blob || blob.size === 0) {
    throw new Error('File not found or empty');
  }
  if (blob.type === 'application/pdf') return blob;
  if (blob.type === 'application/json' || blob.type === 'text/plain') {
    throw new Error('File not found in storage');
  }
  return new Blob([blob], { type: 'application/pdf' });
}

async function messageFromFailedFetch(res, fallback) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) {
    try {
      const data = await res.json();
      if (data?.message) return data.message;
    } catch {
      /* ignore */
    }
  }
  if (res.status === 404) return 'File not found in storage';
  if (res.status === 401) return 'Sign in required';
  if (res.status === 403) return 'You do not have access to this file';
  return fallback;
}

/**
 * PDF stage for MobileMediaViewerOverlay. Fetches authenticated download URLs into a blob
 * so preview works when the API sets X-Frame-Options: DENY on direct iframe loads.
 */
export default function MobileMediaViewerPdf({
  src,
  title = 'PDF',
  className = '',
  iframeClassName = 'mobile-media-viewer-overlay__pdf',
  fetchCredentials = 'include',
  pendingMessage = '',
}) {
  const [iframeSrc, setIframeSrc] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const ownedBlobRef = useRef('');

  useEffect(() => {
    if (ownedBlobRef.current) {
      URL.revokeObjectURL(ownedBlobRef.current);
      ownedBlobRef.current = '';
    }
    setIframeSrc('');
    setError('');
    setLoading(false);

    if (!src) return undefined;

    let cancelled = false;

    const setOwnedIframeSrc = (url) => {
      ownedBlobRef.current = url;
      setIframeSrc(url);
    };

    const loadBlob = (blobPromise) => {
      setLoading(true);
      blobPromise
        .then((blob) => {
          if (cancelled) return;
          setOwnedIframeSrc(URL.createObjectURL(pdfBlobFromBytes(blob)));
        })
        .catch((e) => {
          if (!cancelled) setError(e.message || 'PDF preview unavailable');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    if (isDirectPdfSrc(src)) {
      if (src.startsWith('data:')) {
        setIframeSrc(src);
        return () => {
          cancelled = true;
        };
      }
      loadBlob(fetch(src).then((res) => res.blob()));
    } else {
      loadBlob(
        fetch(src, { credentials: fetchCredentials }).then(async (res) => {
          if (!res.ok) {
            throw new Error(await messageFromFailedFetch(res, 'PDF preview unavailable'));
          }
          return res.blob();
        })
      );
    }

    return () => {
      cancelled = true;
      if (ownedBlobRef.current) {
        URL.revokeObjectURL(ownedBlobRef.current);
        ownedBlobRef.current = '';
      }
      setIframeSrc('');
    };
  }, [src, fetchCredentials]);

  const rootClass = ['mobile-media-viewer-overlay__pdf-wrap', className].filter(Boolean).join(' ');

  if (!src && pendingMessage) {
    return (
      <div className={rootClass}>
        <p className="mobile-media-viewer-overlay__pdf-status">{pendingMessage}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={rootClass}>
        <p className="mobile-media-viewer-overlay__pdf-status">Loading PDF…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={rootClass}>
        <p className="mobile-media-viewer-overlay__pdf-status mobile-media-viewer-overlay__pdf-status--error">
          {error}
        </p>
      </div>
    );
  }

  if (!iframeSrc) return null;

  return (
    <div className={rootClass}>
      <iframe title={title} src={iframeSrc} className={iframeClassName} />
    </div>
  );
}
